import React, { useState } from "react";

import {
  FormContext,
  useForm,
} from "react-hook-form/dist/react-hook-form.ie11";
import { cloneDeep } from "lodash";
import { useFeature } from "flagged";

import {
  DiscardPromptModal,
  UnsavedChangesPromptModal,
} from "@hubcapp/components";
import { useQueryInstructorDetails } from "@hubcapp/hooks";

import { InstructorDetails, MyDetailsFormFields } from "./types";
import { getLatestVehicleMileage } from "./helpers";
import { useAuth } from "@hubcapp/context";
import {
  useMutationUpdateInstructorDetails,
  useQueryGetAgreementsDocumentsByPerson,
  useQueryGetCustomerAndFranchiseDocumentsByPerson,
} from "./hooks";
import MyDetails from "./MyDetails";
import useMutationUpdateMileage from "./hooks/useMutationUpdateMileage";

const StateProvider = () => {
  const [cacheData, setCacheData] = useState<MyDetailsFormFields>();
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [
    loadingDocumentsByInstructor,
    setLoadingDocumentsByInstructor,
  ] = useState(false);
  const {
    instructor: { id: instructorId },
  } = useAuth();
  const isFleetDatabaseSchemaMigrationEnabled = useFeature(
    "fleetDatabaseSchemaMigrationEnabled",
  );

  const form = useForm<MyDetailsFormFields>({
    mode: "onChange",
  });
  const { dirty, isValid } = form.formState;

  const setFormValues = (values: MyDetailsFormFields) => {
    form.reset(values, {
      dirty: false,
    });
    form.register({ name: "vehicleAssignments" });
  };

  const onQueryCompleted = (data: { instructor?: InstructorDetails }) => {
    if (data?.instructor && data?.instructor.vehicleAssignments) {
      const { vehicleAssignments } = data.instructor;
      setCacheData({ vehicleAssignments: cloneDeep(vehicleAssignments) });
      setFormValues({ vehicleAssignments: cloneDeep(vehicleAssignments) });
    }
  };

  const { data, loading } = useQueryInstructorDetails({
    onCompleted: onQueryCompleted,
    fetchPolicy: "network-only",
    variables: {
      id: instructorId,
    },
  });
  const {
    data: documentsByInstructor,
    refetch: refetchInstructorDocs,
  } = useQueryGetAgreementsDocumentsByPerson(instructorId);

  const {
    data: customerAndFranchiseDocumentsByPerson,
    refetch: refetchCustomerAndFranchiseDocs,
    loading: loadingCustomerAndFranchiseDocs,
  } = useQueryGetCustomerAndFranchiseDocumentsByPerson(instructorId);

  const refetchDocs = () => {
    setLoadingDocumentsByInstructor(true);
    Promise.all([
      refetchCustomerAndFranchiseDocs(),
      refetchInstructorDocs(),
    ]).finally(() => setLoadingDocumentsByInstructor(false));
  };

  const onMutationCompleted = (data: {
    updateInstructor?: InstructorDetails;
  }) => {
    if (data && data.updateInstructor) {
      setFormValues(data.updateInstructor);
    }
  };

  const [
    updateInstructorDetails,
    { loading: updateLoading },
  ] = useMutationUpdateInstructorDetails({
    onCompleted: onMutationCompleted,
  });

  const [
    updateMileage,
    { loading: isSavingMileage },
  ] = useMutationUpdateMileage({
    onCompleted: () => {},
  });

  const handleDiscardClick = () => {
    if (!dirty) return;
    setIsDiscarding(true);
  };

  const handleDiscard = () => {
    cacheData && setFormValues(cloneDeep(cacheData));
    setIsDiscarding(false);
  };

  const handleDiscardCancel = () => setIsDiscarding(false);

  const handleSubmit = (data: MyDetailsFormFields) => {
    if (!dirty || !data.vehicleAssignments) return;
    data.vehicleAssignments.forEach(({ id, vehicle }) => {
      if (!vehicle.mileage) return;

      const originalVehicleAssignment = (
        cacheData?.vehicleAssignments || []
      ).find(vA => vA.id === id);

      const originalMileage = getLatestVehicleMileage(
        originalVehicleAssignment?.vehicle?.mileage || [],
      );
      const newMileage = getLatestVehicleMileage(vehicle.mileage);

      if (!newMileage || originalMileage?.value === newMileage?.value) return;

      if (isFleetDatabaseSchemaMigrationEnabled) {
        updateMileage({
          variables: {
            input: {
              vehicleKey: vehicle.id,
              mileage: newMileage.value,
            },
          },
        });
      } else {
        updateInstructorDetails({
          variables: {
            id: instructorId,
            vehicleMileage: {
              id: vehicle.id,
              mileage: newMileage.value,
            },
          },
        });
      }
    });
  };

  const details = data?.instructor;

  return (
    <FormContext {...form}>
      <UnsavedChangesPromptModal
        showPrompt={dirty}
        onSaveClick={form.handleSubmit(handleSubmit)}
        onDiscardClick={handleDiscard}
      />
      {isDiscarding && (
        <DiscardPromptModal
          onCancelClick={handleDiscardCancel}
          onConfirmClick={handleDiscard}
        />
      )}
      <MyDetails
        {...details}
        valid={isValid}
        dirty={dirty}
        loading={
          loading ||
          loadingCustomerAndFranchiseDocs ||
          loadingDocumentsByInstructor ||
          isSavingMileage ||
          updateLoading
        }
        updateLoading={updateLoading}
        onDiscardClick={handleDiscardClick}
        onMileageUpdate={form.handleSubmit(handleSubmit)}
        documentsByInstructor={
          documentsByInstructor?.getAgreementsDocumentsByPerson
        }
        instructorId={instructorId}
        refetchDocs={refetchDocs}
        customerAndFranchiseDocumentsByPerson={
          customerAndFranchiseDocumentsByPerson?.getCustomerAndFranchiseDocumentsByPerson
        }
      />
    </FormContext>
  );
};

export default StateProvider;
